/* eslint-disable @typescript-eslint/indent */
/* eslint-disable react/jsx-curly-newline */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable operator-linebreak */
/* eslint-disable max-len */
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
    Text,
    Textarea,
    VStack,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker, ServiceCheckbox } from '@acp/core-ui';
import { ServiceIdMapper } from '@acp/shared';
import { useProperty } from '../../hooks/use-property';
import { RequestHoldingServiceFormFormValues } from './form-schema';
import { PropertyAddressCard } from '../property-address-card';

function RequestServiceOnHoldScreen({
    propertyId,
    onClose,
    onSubmit,
}: {
    propertyId: number;
    onClose: () => void;
    onSubmit: (vals: RequestHoldingServiceFormFormValues) => void;
}) {
    const form = useFormContext<RequestHoldingServiceFormFormValues>();
    const propertyHoldingServiceQuery = useProperty(propertyId);

    const hasEligibleHoldingService = useMemo(() => {
        if (propertyHoldingServiceQuery.data === undefined) return false;

        // if all services are on hold
        const isAllServicesAreOnHold = propertyHoldingServiceQuery.data.services.every(
            (service) => service.tags.includes('ON_HOLD')
        );

        if (isAllServicesAreOnHold) return false;

        return true;
    }, [propertyHoldingServiceQuery.data]);

    return (
        <>
            <ModalHeader>Request service hold</ModalHeader>

            <ModalBody>
                <PropertyAddressCard w="full" mb={5} propertyId={propertyId} />

                {/* loading data */}
                <Box w="full">
                    {(propertyHoldingServiceQuery.isLoading ||
                        propertyHoldingServiceQuery.data === undefined) && (
                        <Flex
                            alignItems="center"
                            fontWeight="bold"
                            justifyContent="center"
                            py={8}
                            px={8}
                            borderColor="muted.50"
                            borderWidth={1}
                            rounded="md"
                        >
                            <Spinner />
                        </Flex>
                    )}

                    {propertyHoldingServiceQuery.data !== undefined &&
                        !hasEligibleHoldingService && (
                            <FormControl mb={2}>
                                <FormLabel htmlFor="none">
                                    Services eligible to hold
                                </FormLabel>
                                <Flex
                                    flexDir="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    py={8}
                                    px={8}
                                    borderColor="muted.50"
                                    borderWidth={1}
                                    rounded="md"
                                >
                                    <Text color="red.500" fontWeight="bold">
                                        No services available to hold
                                    </Text>
                                    <Text fontSize="sm" color="black">
                                        All services for this property are already on hold
                                    </Text>
                                </Flex>
                            </FormControl>
                        )}

                    {propertyHoldingServiceQuery.data !== undefined &&
                        hasEligibleHoldingService && (
                            <VStack gap={6} py={4}>
                                <Controller
                                    name="services"
                                    control={form.control}
                                    render={({ field, fieldState }) => (
                                        <FormControl isInvalid={fieldState.invalid}>
                                            <FormLabel htmlFor="none">
                                                Services eligible to hold
                                            </FormLabel>

                                            {/* select service */}
                                            <HStack gap={2} w="full">
                                                {propertyHoldingServiceQuery.data.services.map(
                                                    (service) => (
                                                        <ServiceCheckbox
                                                            key={service.serviceId}
                                                            bgColor="white"
                                                            labelColorScheme="muted"
                                                            serviceName={ServiceIdMapper.getFormattedServiceName(
                                                                service.shortName
                                                            )}
                                                            isChecked={field.value.includes(
                                                                service.serviceId
                                                            )}
                                                            onChange={(isToBeHold) => {
                                                                const newValue =
                                                                    isToBeHold
                                                                        ? [
                                                                              ...field.value,
                                                                              service.serviceId,
                                                                          ]
                                                                        : field.value.filter(
                                                                              (v) =>
                                                                                  v !==
                                                                                  service.serviceId
                                                                          );

                                                                field.onChange(newValue);
                                                            }}
                                                            labelText={
                                                                service.tags.includes(
                                                                    'ON_HOLD'
                                                                )
                                                                    ? 'On hold'
                                                                    : undefined
                                                            }
                                                        />
                                                    )
                                                )}
                                            </HStack>

                                            {fieldState.error && (
                                                <FormErrorMessage>
                                                    {fieldState.error.message}
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    )}
                                />

                                {/* form */}
                                <Controller
                                    name="holdDate"
                                    control={form.control}
                                    render={({ field, fieldState }) => (
                                        <FormControl
                                            isRequired
                                            isInvalid={fieldState.invalid}
                                        >
                                            <FormLabel>Hold service until</FormLabel>

                                            <DatePicker
                                                {...field}
                                                placeholder="Select date"
                                                isInvalid={fieldState.invalid}
                                                dateDisplayFormat="do MMMM yyyy"
                                                size="md"
                                                fromDate={new Date()}
                                            />

                                            {fieldState.error && (
                                                <FormErrorMessage>
                                                    {fieldState.error.message}
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    )}
                                />

                                <Controller
                                    name="reasonNote"
                                    control={form.control}
                                    render={({ field, fieldState }) => (
                                        <FormControl
                                            isRequired
                                            isInvalid={fieldState.invalid}
                                        >
                                            <FormLabel>Reason</FormLabel>
                                            <Textarea
                                                {...field}
                                                isInvalid={fieldState.invalid}
                                            />

                                            {fieldState.error && (
                                                <FormErrorMessage>
                                                    {fieldState.error.message}
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    )}
                                />
                            </VStack>
                        )}
                </Box>
            </ModalBody>

            <ModalFooter justifyContent="space-between">
                <Button colorScheme="muted" variant="ghost" onClick={onClose}>
                    Close
                </Button>

                <Button
                    onClick={form.handleSubmit(onSubmit)}
                    colorScheme="secondary"
                    isDisabled={
                        propertyHoldingServiceQuery.isPending ||
                        !hasEligibleHoldingService
                    }
                >
                    Next
                </Button>
            </ModalFooter>
        </>
    );
}

export { RequestServiceOnHoldScreen };
